import * as React from 'react'

import {
  F0FilterPickerContent,
  type FiltersDefinition,
  type FiltersState,
} from '@factorialco/f0-react'

import {
  type FilterState,
  type Specification,
  filtersToSpecification,
  parseSpecification,
  specificationToFilters,
  stringifySpecification,
} from 'modules/posts/components/Groups/DynamicGroupsAudience/specificationTransformers'
import {
  type DynamicAudienceFilters,
  useDynamicAudienceFilters,
} from 'modules/posts/components/Groups/DynamicGroupsAudience/useDynamicAudienceFilters'

/**
 * Default filter keys that are shown when enabledFilters is not specified.
 * Note: 'team' is excluded by default.
 */
export const DEFAULT_ENABLED_FILTERS: Array<keyof DynamicAudienceFilters> = [
  'location',
  'legal_entity',
  'direct_reports_of',
  'all_reportees_of',
]

/**
 * All available built-in filter keys.
 */
export const ALL_BUILTIN_FILTERS: Array<keyof DynamicAudienceFilters> = [
  'location',
  'team',
  'legal_entity',
  'direct_reports_of',
  'all_reportees_of',
]

export interface SpecificationSelectorProps {
  specification: string | null | undefined
  onChange: (specification: string) => void
  onTransformError?: (canDisplay: boolean) => void
  disabled?: boolean
  enabledFilters?: string[]
  additionalFilters?: FiltersDefinition
}

/**
 * SpecificationSelector is a reusable component for defining dynamic team audiences.
 *
 * It handles:
 * - Transforming specification JSON to filter selector format
 * - Displaying the filter picker UI
 * - Transforming filter selections back to specification JSON
 * - Preserving whitelist/blacklist from the original specification
 *
 * This component is designed to be product-agnostic and can be used across
 * different features that need dynamic audience definition.
 *
 * Error handling is delegated to the parent via onTransformError callback.
 *
 * @example
 * // Basic usage with default filters
 * <SpecificationSelector
 *   specification={spec}
 *   onChange={handleChange}
 * />
 *
 * @example
 * // Enable specific filters including 'team'
 * <SpecificationSelector
 *   specification={spec}
 *   onChange={handleChange}
 *   enabledFilters={['location', 'team', 'direct_reports_of']}
 * />
 *
 * @example
 * // Add custom filters from parent -> See InFilterDefinition
 * const customFilters = {
 *   custom_criteria: {
 *     type: 'in' as const,
 *     label: 'Custom Filter',
 *     options: {
 *       source: useGraphqlDataSource({
 *         paginationType: 'infinite-scroll',
 *         fetchPolicy: 'network-only',
 *         document: YOUR_QUERY_DOCUMENT,
 *         perPage: 25,
 *         mapData: (data) => {
 *           return data.YOUR_QUERY_DOCUMENT_DATA_MAPPER_LAMBDA
 *         },
 *       }) }
 *       mapOptions: (team: TeamNode) => ({
 *         label: YOUR_LABEL,
 *         value: YOUR_VALUE_NUMBER,
 *       }),
 *     }
 *   }
 * }
 * <SpecificationSelector
 *   specification={spec}
 *   onChange={handleChange}
 *   additionalFilters={customFilters}
 *   enabledFilters={['location', 'custom_criteria']}
 * />
 */
export const SpecificationSelector: React.FC<SpecificationSelectorProps> = ({
  specification,
  onChange,
  onTransformError,
  disabled: _disabled = false,
  enabledFilters,
  additionalFilters,
}) => {
  const builtInFilters = useDynamicAudienceFilters()

  const allFilters = React.useMemo((): FiltersDefinition => {
    if (!additionalFilters) {
      return builtInFilters
    }
    return {
      ...builtInFilters,
      ...additionalFilters,
    }
  }, [builtInFilters, additionalFilters])

  const visibleFilters = React.useMemo((): FiltersDefinition => {
    const keysToShow = enabledFilters ?? DEFAULT_ENABLED_FILTERS
    const filtered: FiltersDefinition = {}

    for (const key of keysToShow) {
      const filterDef = allFilters[key]
      if (filterDef) {
        filtered[key] = filterDef
      }
    }

    return filtered
  }, [allFilters, enabledFilters])

  const validFilterKeys = React.useMemo(() => {
    const builtInKeys = ALL_BUILTIN_FILTERS.map(String)
    const additionalKeys = additionalFilters ? Object.keys(additionalFilters) : []
    return [...builtInKeys, ...additionalKeys]
  }, [additionalFilters])

  const [state, setState] = React.useState<{
    filters: FiltersState<FiltersDefinition>
    preservedWhitelist: number[] | undefined
    preservedBlacklist: number[] | undefined
  }>({
    filters: {},
    preservedWhitelist: undefined,
    preservedBlacklist: undefined,
  })

  React.useEffect(() => {
    if (!specification) {
      setState({
        filters: {},
        preservedWhitelist: undefined,
        preservedBlacklist: undefined,
      })
      return
    }

    const spec = parseSpecification(specification)

    if (!spec) {
      setState({
        filters: {},
        preservedWhitelist: undefined,
        preservedBlacklist: undefined,
      })
      onTransformError?.(false)
      return
    }

    const result = specificationToFilters(spec)

    const newFilters: FiltersState<FiltersDefinition> = {}

    if (!result.success) {
      onTransformError?.(false)
    } else {
      if (result.filters) {
        for (const filterKey of validFilterKeys) {
          const values = result.filters[filterKey]
          if (values && values.length > 0) {
            newFilters[filterKey] = values
          }
        }
      }
      onTransformError?.(true)
    }

    setState({
      filters: newFilters,
      preservedWhitelist: result.whitelist,
      preservedBlacklist: result.blacklist,
    })
  }, [specification, onTransformError, validFilterKeys])

  const handleFilterChange = React.useCallback(
    (newFilters: FiltersState<FiltersDefinition>) => {
      setState((prev) => ({ ...prev, filters: newFilters }))

      const filterStateRecord: FilterState = {}
      for (const [key, values] of Object.entries(newFilters)) {
        if (Array.isArray(values) && values.length > 0) {
          filterStateRecord[key] = values.map((v) => (typeof v === 'number' ? v : Number(v)))
        }
      }

      const spec = filtersToSpecification(filterStateRecord, {
        whitelist: state.preservedWhitelist,
        blacklist: state.preservedBlacklist,
      })

      onChange(stringifySpecification(spec))
    },
    [onChange, state.preservedWhitelist, state.preservedBlacklist]
  )

  return (
    <F0FilterPickerContent
      filters={visibleFilters}
      value={state.filters}
      onChange={handleFilterChange}
      showApplyButton={false}
    />
  )
}

/**
 * Utility function to check if a specification is empty (no criterias and no whitelist)
 */
export function isSpecificationEmpty(specificationJson: string | null | undefined): boolean {
  if (!specificationJson) {
    return true
  }

  const spec = parseSpecification(specificationJson)
  if (!spec) {
    return true
  }

  return !spec.criterias && !spec.whitelist?.length
}

/**
 * Export types that consumers might need
 */
export type { Specification, DynamicAudienceFilters }
